import React, {useContext, useEffect, useState} from "react";
import {Box, Button, useTheme} from "@mui/material";
import {AddRounded} from "@mui/icons-material";
import {useMutation} from "@tanstack/react-query";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FormProvider, useFieldArray, useForm, useWatch} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {AxiosError} from "axios";
import _ from "underscore";
import CustomLoader from "../../../../util/component/CustomLoader";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {ICustomerData, ICustomerDataForm, IPerson} from "../../models/models";
import {updateDraftRA, validateDocumentsByOwner} from "../services/http.services";
import {
    bookingDataValueState,
    createRaPricingInfoErrorState,
    createRaPricingInfoState,
    customerDataAccordionState,
    customerDataValueState,
    recoveryRaInfoState,
    recoveryRaModeState,
    stepState
} from "../state";
import {ADDITIONAL_DRIVER_INIT_VALUE, CUSTOMER_DATA_FORM_INITIAL_VALUES, CUSTOMER_DATA_FORM_VALIDATION_SCHEMA, DRIVER_VALIDATION_SCHEMA} from "./config";
import {ERROR_DETAIL_LIST, CUSTOMER_STATES} from '../config';
import DriverData from "./driver-data/DriverData";
import {LOCALE_ISO_DATE_TIME_FORMAT} from "../../config";
import {deepCloneValue, isImmutableRa} from "../../utils";
import {deepClone} from "../../../../util/state/commonFunctions";
import {CREDIT_TYPE_VALUES} from "../ra-filling/config";
import {setSnackbar} from "../../../../util/state/globalAction";
import {getChengesData} from "../services/utils";
import dayjs from "dayjs";
import {useSearchParams} from "react-router-dom";

/**
 * Create RA Component
 */
const CustomerData = (props: { keyLess: boolean | undefined, keyLessInfo: string | undefined }) => {
    const theme = useTheme();
    const { t } = useTranslation(['network', 'translation']);

    // Use Context
    const { dispatchGlobalData } = useContext(GlobalContext);
    const [searchParams] = useSearchParams();

    const [step, setStep] = useRecoilState(stepState);
    const recoveryRaInfo = useRecoilValue(recoveryRaInfoState);
    const setMainDriverAccordion = useSetRecoilState(customerDataAccordionState('mainDriver'));
    const [driverAccordions, setDriverAccordions]: any = useRecoilState(customerDataAccordionState('addDrivers'));
    const [bookingDataValue, setBookingDataValue]: any = useRecoilState(bookingDataValueState);
    const createRaPricingInfo = useRecoilValue(createRaPricingInfoState);
    // form values state
    const [customerDataValue, setCustomerDataValue] = useRecoilState(customerDataValueState);
    const createRaPricingInfoError = useRecoilValue(createRaPricingInfoErrorState);
    const recoveryMode = useRecoilValue(recoveryRaModeState);

    const [flagUpdateDefaultFormValue, setFlagUpdateDefaultFormValue] = useState<boolean>(false);

    const [currDriverToValidateDocuments, setCurrDriverToValidateDocuments] = useState<{id: string, index: number | undefined}>({
        id: '',
        index: undefined
    });

    const customerDataForm = useForm<ICustomerDataForm>({
        defaultValues: CUSTOMER_DATA_FORM_INITIAL_VALUES,
        resolver: yupResolver(
            props.keyLess
                ? CUSTOMER_DATA_FORM_VALIDATION_SCHEMA
                    .omit(['mainDriver'])
                    .concat(yup.object({
                        mainDriver: DRIVER_VALIDATION_SCHEMA
                            .omit(['citizenship', 'issuingPlace'])
                            .concat(yup.object().shape({
                                citizenship: yup.object().nullable(),
                                issuingPlace: yup.object().nullable(),
                            }))
                    }))
                : CUSTOMER_DATA_FORM_VALIDATION_SCHEMA,
            {},
            { rawValues: true }
        ),
        context: {
            recoveryMode,
            effectivePickupDate: recoveryRaInfo?.effectivePickupDate
        }
    })

    const {errors, dirtyFields} = customerDataForm.formState;
    const [mainDriverCbId, mainDriverVerifiedDocuments] = useWatch({control: customerDataForm.control, name: ["mainDriver.cbId", "mainDriver.verifiedDocuments"]});
    const [isNextButtonDisabled] = useState<boolean>(!recoveryMode && searchParams.get("enable_btn") === null && !!bookingDataValue?.bookingDetail?.dropoffDate && dayjs(bookingDataValue.bookingDetail.dropoffDate).isBefore(dayjs()));

    const { append, remove } = useFieldArray<ICustomerDataForm>({
        control: customerDataForm.control,
        name: "additionalDrivers"
    });

    const additionalDrivers = customerDataForm.getValues('additionalDrivers');

    const viewErrorMessage = (type: 0 | 1 | 2 | 3 | 4 = snackbarTypes.error, message: string) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: message,
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const deleteAdditionalDriver = (ind: number) => {
        const cdv = deepCloneValue(customerDataForm.getValues());

        if (ind !== null && ind !== undefined && !!cdv?.additionalDrivers[ind as number]) {
            cdv.additionalDrivers.splice(ind, 1);
            const drAcc = deepCloneValue(driverAccordions);
            drAcc.splice(ind, 1);
            setCustomerDataValue(cdv);
            setDriverAccordions(drAcc);
        }
    }

    const setDriverData = (type: string, driver: ICustomerData) => {
        if (type !== 'mainDriver') {
            append(ADDITIONAL_DRIVER_INIT_VALUE);
        }

        for (const [key, value] of Object.entries(driver)) {
            // @ts-ignore
            customerDataForm.setValue(`${type}.${key}`, value);
        }
    }

    function buildPayloadWidthFormData(reqParams: any, drivers: ICustomerDataForm) {
        reqParams.raOwner = fillPersonPayloadData(drivers.mainDriver);
        reqParams.raOwner.type = "RA_OWNER";

        const payloadKeys = ['firstDriver', 'driver'];
        drivers.additionalDrivers
            .map((elem: ICustomerData, ind: number) => {
                const driver = fillPersonPayloadData(elem);
                driver.type = "DRIVER";

                reqParams[payloadKeys[ind]] = driver;
                return elem;
            });

        if (!drivers.additionalDrivers[0]) {
            if (!!reqParams.firstDriver) {
                delete reqParams.firstDriver;
            }
        }
        if (!drivers.additionalDrivers[1]) {
            if (!!reqParams.driver) {
                delete reqParams.driver;
            }
        }
    }

    /**  Map person data from FE to BE */
    function fillPersonPayloadData(driver: ICustomerData): IPerson {
        return {
            firstName: driver.name,
            lastName: driver.surname,
            gender: driver.gender,
            birthDate: !!driver.birthDate ? (typeof driver.birthDate === "string" ? driver.birthDate : driver.birthDate.format(LOCALE_ISO_DATE_TIME_FORMAT)) : null,
            birthNation: driver.birthNation,

            // birthPlace: driver.birthPlace, // fixme: delete dopo lettura della lista delle città
            birthPlace: { code: driver.birthPlace, description: driver.birthPlace },

            countryResidence: driver.residenceCountry,

            // cityResidence: driver.residenceCity, // fixme: delete dopo lettura della lista delle città
            cityResidence: { code: driver.residenceCity, description: driver.residenceCity },

            provinceResidence: driver.residenceProvince ? {
                code: driver.residenceProvince.codeProvince,
                description: driver.residenceProvince.description,
            } : null,
            address: driver.address,
            streetNumber: driver.streetNumber,
            postCode: driver.postcode,
            mobile: driver.mobile,
            prefix: driver.prefix,
            email: driver.email,
            fiscalCode: driver.fiscalCode,
            customerType: driver.customerType,
            customerId: driver.customerID,
            cbId: driver.cbId,
            citizenship: driver.citizenship,
            drivingLicenseNumber: driver.drivingLicenseNumber,
            drivingLicenseCategory: driver.drivingLicenseCategory,
            drivingLicenseIssuingAuthority: driver.issuingAuthority,
            drivingLicenseIssueDate: !!driver.issuingDate ? (typeof driver.issuingDate === "string" ? driver.issuingDate : driver.issuingDate.format(LOCALE_ISO_DATE_TIME_FORMAT)) : null,
            drivingLicenseExpiryDate: !!driver.expireDate ? (typeof driver.expireDate === "string" ? driver.expireDate : driver.expireDate.format(LOCALE_ISO_DATE_TIME_FORMAT)) : null,
            drivingLicenseIssuingPlace: driver.issuingPlace,
            drivingLicenseFirstReleaseDate: driver.drivingLicenseFirstReleaseDate?.format(LOCALE_ISO_DATE_TIME_FORMAT) ?? null,
            drivingLicenseDocFront: {
                attachmentId: driver.drivingLicenseDocCode,
                cbId: driver.cbId,
                dossierIdentifier: driver.dossierIdentifier,
                frontBack: "FRONT_PAGE",
                documentFile: null,
                documentTitle: driver.drivingLicenseDocTitleFront || null,
                fileSize: driver.drivingLicenseDocFront?.size ?? null,
                contentType: driver.drivingLicenseDocFront?.type ?? null,
                path: driver.drivingLicenseDocPathFront || null
            },
            drivingLicenseDocBack: {
                attachmentId: driver.drivingLicenseDocCode,
                cbId: driver.cbId,
                dossierIdentifier: driver.dossierIdentifier,
                frontBack: "BACK_PAGE",
                documentFile: null,
                documentTitle: driver.drivingLicenseDocTitleBack || null,
                fileSize: driver.drivingLicenseDocBack?.size ?? null,
                contentType: driver.drivingLicenseDocBack?.type ?? null,
                path: driver.drivingLicenseDocPathBack || null
            },
            idNumber: driver.idNumber,
            idType: driver.idType,
            idIssueDate: driver.idIssueDate?.format(LOCALE_ISO_DATE_TIME_FORMAT) ?? null,
            idExpiryDate: driver.idExpiryDate?.format(LOCALE_ISO_DATE_TIME_FORMAT) ?? null,
            idIssuingAuthority: driver.idIssuingAuthority,
            idIssuingPlace: driver.idIssuingPlace,
            idDocFront: {
                attachmentId: driver.idDocCode,
                cbId: driver.cbId,
                dossierIdentifier: driver.dossierIdentifier,
                frontBack: "FRONT_PAGE",
                documentFile: null,
                documentTitle: driver.idDocTitleFront || null,
                fileSize: driver.idDocFront?.size ?? null,
                contentType: driver.idDocFront?.type ?? null,
                path: driver.idDocPathFront || null
            },
            idDocBack: {
                attachmentId: driver.idDocCode,
                cbId: driver.cbId,
                dossierIdentifier: driver.dossierIdentifier,
                frontBack: "BACK_PAGE",
                documentFile: null,
                documentTitle: driver.idDocTitleBack || null,
                fileSize: driver.idDocBack?.size ?? null,
                contentType: driver.idDocBack?.type ?? null,
                path: driver.idDocPathBack || null
            }
        }
    }

    const updateDraftRaMutation = useMutation(
        updateDraftRA,
        {
            onSuccess: (response: any) => {
                // recovery of data if they had not passed through booking
                customerDataForm.setValue('mainDriver.customerID', response.raOwner.customerID);
                customerDataForm.setValue('mainDriver.cbId', response.raOwner.cbId);
                customerDataForm.setValue('mainDriver.customerType', response.raOwner.customerType);
                customerDataForm.setValue('mainDriver.verifiedDocuments', response.raOwner.verifiedDocuments);

                ['firstDriver', 'driver']
                    .map((elem: string, ind: number) => {
                        if (!!response[elem]) {
                            customerDataForm.setValue(`additionalDrivers.${ind}.customerID`, response[elem].cbId);
                            customerDataForm.setValue(`additionalDrivers.${ind}.customerType`, response[elem].customerType);
                            customerDataForm.setValue(`additionalDrivers.${ind}.verifiedDocuments`, response[elem].verifiedDocuments);
                        }
                        return elem;
                    });

                // store form data Recoil State
                setCustomerDataValue(customerDataForm.getValues());
                setBookingDataValue(response);

                // set next step
                if (step.active < (step.numSteps - 1)) {
                    const completed = step.completed.concat();
                    completed[step.active + 1] = true;
                    setStep({
                        active: step.active + 1,
                        completed,
                        numSteps: step.numSteps
                    });
                }
            },
            onError: (err) => {
                let errorMessage = t('customerData.message.error.tryLater');
                if (err instanceof AxiosError) {
                    if(Array.isArray(err.response?.data)) {
                        err.response?.data.forEach?.((e: any) => {
                            switch (e.code) {
                                case 'main_driver_fiscal_code':
                                    errorMessage = t('customerData.message.error.fiscalCode');
                                    customerDataForm.setError("mainDriver.fiscalCode", {
                                        message: 'common.error.invalidFS',
                                    })
                                    break;
                                case 'additional_driver_1_fiscal_code':
                                    errorMessage = t('customerData.message.error.fiscalCode');
                                    customerDataForm.setError("additionalDrivers.0.fiscalCode", {
                                        message: 'common.error.invalidFS',
                                    })
                                    break;
                                case 'additional_driver_2_fiscal_code':
                                    errorMessage = t('customerData.message.error.fiscalCode');
                                    customerDataForm.setError("additionalDrivers.1.fiscalCode", {
                                        message: 'common.error.invalidFS',
                                    })
                                    break;
                                case 'main_driver_duplicate':
                                    errorMessage = t('customerData.message.error.mainDriverDuplicate');
                                    break;
                                case 'main_driver_identity_card_duplicate':
                                    errorMessage = t('customerData.message.error.mainDriverIdentityCardDuplicate');
                                    break;
                                case 'additional_driver_1_duplicate':
                                    errorMessage = t('customerData.message.error.additionalDriver1Duplicate');
                                    break;
                                case 'additional_driver_1_identity_card_duplicate':
                                    errorMessage = t('customerData.message.error.additionalDriver1IdentityCardDuplicate');
                                    break;
                                case 'additional_driver_2_duplicate':
                                    errorMessage = t('customerData.message.error.additionalDriver2Duplicate');
                                    break;
                                case 'additional_driver_2_identity_card_duplicate':
                                    errorMessage = t('customerData.message.error.additionalDriver2IdentityCardDuplicate');
                                    break;
                            }
                        })
                    } else if((err?.response?.data)?.hasOwnProperty('errorDetails')) {
                        const {errorDetails, errorMessage: errorAddMessage} = (err?.response?.data);
                        if(!!ERROR_DETAIL_LIST?.hasOwnProperty(errorDetails)) {
							const errorDetailLs = ERROR_DETAIL_LIST[errorDetails as keyof typeof ERROR_DETAIL_LIST];
							if('addingMessage' in errorDetailLs) {
                                const detail = errorDetailLs.addingMessage(errorAddMessage);
								errorMessage = t(`customerData.message.error.${errorDetailLs.message}`, {detail});
							} else {
								errorMessage = t(`customerData.message.error.${errorDetailLs.message}`);
							}
                        }
                    }
                }
                viewErrorMessage(snackbarTypes.error,  errorMessage);
            }
        }
    );

    useEffect(() => {
		if (!!errors && !_.isEmpty(errors)) {
            
			setMainDriverAccordion(Boolean(errors.mainDriver));

            const accs = driverAccordions.concat();
            if(errors.additionalDrivers != undefined && Boolean(errors.additionalDrivers[0])) accs[0] = true;
            if(errors.additionalDrivers != undefined && Boolean(errors.additionalDrivers[1])) accs[1] = true;
            setDriverAccordions(accs);

			viewErrorMessage(snackbarTypes.error,  t('common.error.formDataIsInvalid'));
		}
	}, [errors]);

    const handleNextStep = (data: ICustomerDataForm) => {
        const reqParams: any = deepClone(bookingDataValue);
        if(reqParams.state === CUSTOMER_STATES.SUSPENDED) {
            viewErrorMessage(snackbarTypes.error,  t('customerData.message.error.suspended'));
			return;
        }

        if (!checkMoreDriversThatAllowed()) return;

        let changesData: string[] = [];
        getChengesData(changesData, 'raOwner', dirtyFields.mainDriver);
        if (!!customerDataForm?.formState?.dirtyFields?.additionalDrivers?.[0]) {
            getChengesData(changesData, 'firstDriver', dirtyFields.additionalDrivers?.[0]);
        }
        if (!!customerDataForm?.formState?.dirtyFields?.additionalDrivers?.[1]) {
            getChengesData(changesData, 'driver', dirtyFields.additionalDrivers?.[1]);
        }

        buildPayloadWidthFormData(reqParams, data);
        reqParams['changesData'] = changesData;
        updateDraftRaMutation.mutate({
            recoveryMode: recoveryMode,
            ra: JSON.stringify(reqParams),
            note: recoveryRaInfo?.reason,
            // effectivePickupDatetime: recoveryRaInfo?.effectivePickupDate?.toISOString(),
            recoveryDate:            !!recoveryRaInfo?.effectivePickupDate ?  recoveryRaInfo?.effectivePickupDate.toISOString() : undefined,
            effectivePickupDatetime: !!recoveryRaInfo?.effectivePickupDate ?  recoveryRaInfo?.effectivePickupDate.toISOString() : undefined,
            idDocFront1: data.mainDriver.idDocFront,
            idDocBack1: data.mainDriver.idDocBack,
            drivingLicenseDocFront1: data.mainDriver.drivingLicenseDocFront,
            drivingLicenseDocBack1: data.mainDriver.drivingLicenseDocBack,
            idDocFront2: data.additionalDrivers[0]?.idDocFront ?? null,
            idDocBack2: data.additionalDrivers[0]?.idDocBack ?? null,
            drivingLicenseDocFront2: data.additionalDrivers[0]?.drivingLicenseDocFront ?? null,
            drivingLicenseDocBack2: data.additionalDrivers[0]?.drivingLicenseDocBack ?? null,
            idDocFront3: data.additionalDrivers[1]?.idDocFront ?? null,
            idDocBack3: data.additionalDrivers[1]?.idDocBack ?? null,
            drivingLicenseDocFront3: data.additionalDrivers[1]?.drivingLicenseDocFront ?? null,
            drivingLicenseDocBack3: data.additionalDrivers[1]?.drivingLicenseDocBack ?? null,
            ...(props.keyLess ? { keyless: props.keyLess, keyLessInfo: props.keyLessInfo } : { })
        } as any);
    }

    /**
     * Check if number of the additional drivers is more that allowed
     * @returns {boolean} true - it's ok, false - check fails
     */
    const checkMoreDriversThatAllowed = () => {
        if (!!customerDataValue.additionalDrivers && Array.isArray(customerDataValue.additionalDrivers) && !!createRaPricingInfo) {
            if (!(customerDataValue.additionalDrivers.length <= createRaPricingInfo?.maxItem)) {
                viewErrorMessage(snackbarTypes.error, t('common.error.moreDriversThatAllowed'));
                return false;
            }
        }
        return true;
    }

    /**
     * Add driver to form
     */
    const handleAddDriver = (e: React.SyntheticEvent) => {
         e.stopPropagation();

        if (customerDataValue.additionalDrivers.length <= createRaPricingInfo?.maxItem) {
            const formValue = deepCloneValue(customerDataForm.getValues());
            formValue.additionalDrivers.push(ADDITIONAL_DRIVER_INIT_VALUE);
            setCustomerDataValue(formValue);

            const drAcc = deepCloneValue(driverAccordions);
            drAcc.push(true);
            setDriverAccordions(drAcc);
         }
    }

    /**
     * Check if booked driver's number is < pricing allowed
     */
    useEffect(() => {
        checkMoreDriversThatAllowed();
    }, [createRaPricingInfo, customerDataValue]);

    useEffect(()=> {
        if (!_.isEmpty(customerDataValue)) {
            if (!flagUpdateDefaultFormValue) {
                    customerDataForm.reset(customerDataValue);
                    setFlagUpdateDefaultFormValue(true);
            } else {
                setDriverData('mainDriver', customerDataValue.mainDriver);

                remove();
                if (!!customerDataValue.additionalDrivers[0]) {
                    setDriverData('additionalDrivers[0]', customerDataValue.additionalDrivers[0]);
                    setDriverAccordions(driverAccordions.concat([true]));
                }

                if (!!customerDataValue.additionalDrivers[1]) {
                    setDriverData(`additionalDrivers[1]`, customerDataValue.additionalDrivers[1]);
                    setDriverAccordions(driverAccordions.concat([false]));
                }
            }
        }
    }, [customerDataValue])

    const validateDocumentsQuery = useMutation({
        mutationFn: (id: string) => {
            return validateDocumentsByOwner(id);
        },
        onSuccess: (data: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("customerData.message.success"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            if(!!currDriverToValidateDocuments.id) {
                if(currDriverToValidateDocuments.index == undefined) {
                    customerDataForm.setValue("mainDriver.verifiedDocuments", true);
                }
                else {
                    customerDataForm.setValue(`additionalDrivers.${currDriverToValidateDocuments.index}.verifiedDocuments`, true);
                }
                const c = deepCloneValue(customerDataForm.getValues())
                setCustomerDataValue(c);
            }

            setCurrDriverToValidateDocuments({id: '', index: undefined});

        },
        onError: (err: any) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("customerData.message.error.tryLater"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            setCurrDriverToValidateDocuments({id: '', index: undefined});
        }

    });

    const handleValidateDocuments = (idDriver: string, index?: number) => setCurrDriverToValidateDocuments({id: idDriver, index: index});

    useEffect(() => {
        if(!!currDriverToValidateDocuments.id)
            validateDocumentsQuery.mutate(currDriverToValidateDocuments.id);
    }, [currDriverToValidateDocuments])

    return (
        <>
            <FormProvider {...customerDataForm}>
                <Box>
                    <DriverData
                        title={t('customerData.accordion.title.mainDriver')}
                        referenceProp="mainDriver"
                        immutableRa={isImmutableRa(bookingDataValue?.financialInfo?.segment, bookingDataValue?.financialInfo?.paymentTiming)}
                        validateDocuments={handleValidateDocuments}
                        keyLess={props.keyLess}
                    />

                    {   !props.keyLess &&
                        additionalDrivers
                        .map((driver: ICustomerData, index: number) =>
                            <DriverData
                                key={`driver-${index}`}
                                title={`${t('customerData.accordion.title.additionalDriver')} ${index + 1}`}
                                referenceProp="additionalDrivers"
                                ind={index}
                                deleteAdditionalDriver={deleteAdditionalDriver}
                                validateDocuments={handleValidateDocuments}
                                keyLess={false /* do not lock additional drivers because of keyless */}
                            />
                        )
                    }
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: theme.spacing(2) }}>
                    {props.keyLess || <Button variant={'text'} startIcon={<AddRounded />}
                            sx={{color: theme.palette.secondary.main, mx: theme.spacing(2)}} onClick={handleAddDriver}
                            disabled={!(customerDataValue.additionalDrivers.length < createRaPricingInfo?.maxItem) ||
                                        bookingDataValue.bookingDetail.flagSubstitution ||
                                        bookingDataValue.financialInfo.creditType === CREDIT_TYPE_VALUES.FULL_CREDIT ||
                                        createRaPricingInfoError
                    }>
                        {t('customerData.button.addDriver')}
                    </Button>}

                    <Button variant={'outlined'}
                            sx={{ color: theme.palette.accent.main }}
                            onClick={customerDataForm.handleSubmit(handleNextStep)}
                            disabled={
                                (!!mainDriverCbId && mainDriverVerifiedDocuments !== null && mainDriverVerifiedDocuments !== true) ||
                                (additionalDrivers.length == 2 ?
                                    (!!additionalDrivers[0].cbId && (additionalDrivers[0].verifiedDocuments !== null && additionalDrivers[0].verifiedDocuments !== true) ||
                                        !!additionalDrivers[1].cbId && (additionalDrivers[1].verifiedDocuments !== null && additionalDrivers[1].verifiedDocuments !== true)) :
                                    (additionalDrivers.length == 1 ?
                                        (!!additionalDrivers[0].cbId && (additionalDrivers[0].verifiedDocuments !== null && additionalDrivers[0].verifiedDocuments !== true)) :
                                        false)
                                 ) ||
                                isNextButtonDisabled
                             }>
                        {t('common.button.nextStep')}
                    </Button>
                </Box>
            </FormProvider>

            <CustomLoader loading={updateDraftRaMutation.isLoading || validateDocumentsQuery.isLoading} />
        </>
    )
}

export default CustomerData;
